import { getGraphClient, parseSiteUrl } from './sharePointService';
import type {
    InjioRegistrySource,
    SearchChunkResult,
    SourceItem,
    CacheRow,
    ListColumn,
    SourceRegistrationRequest,
} from '@shared/dto/searchIndexer';
import {
    SP_LIST_NAME_INJIO_REGISTRY,
    SP_INJIO_EMBEDDINGS_LIST_PREFIX,
    SP_LIST_NAME_EMBEDDINGS,
    SP_LIST_NAME_CORE_SETTINGS,
    SEARCH_INDEX_LEASE_TTL_MS,
} from '../config/constants';

const PREFER_HEADER = 'HonorNonIndexedQueriesWarningMayFailRandomly';

const SYSTEM_COLUMN_NAMES = new Set([
    'ID', 'Title', 'Modified', 'Created', 'Author', 'Editor', 'ContentType',
    '_UIVersionString', 'Attachments', 'Edit', 'LinkTitleNoMenu', 'LinkTitle',
    'DocIcon', '_HasCopyDestinations', '_CopySource', 'owshiddenversion',
    'WorkflowVersion', '_UIVersion', 'ParentLeafName', 'Last_x0020_Modified',
    'Created_x0020_Date', 'FileSizeDisplay', 'SelectTitle', 'SelectFilename',
    '_ComplianceFlags', '_ComplianceTag', '_ComplianceTagWrittenTime',
    '_ComplianceTagUserId',
]);

type TagFieldMeta = { isLookup: boolean; targetListId?: string; targetColumnName?: string };

export class SearchIndexerService {
    private readonly _siteIdCache = new Map<string, string>();
    private readonly _registryListIdCache = new Map<string, string>();
    private readonly _registryUrlCache = new Map<string, string>();
    private readonly _tagFieldMetaCache = new Map<string, TagFieldMeta>();
    private readonly _tagLookupValueCache = new Map<string, string>();
    private readonly _sessionOwner: string;

    constructor() {
        this._sessionOwner = `cat:${crypto.randomUUID()}`;
    }

    // ── Private helpers ──────────────────────────────────────────────────────

    private async _getSiteId(siteUrl: string): Promise<string> {
        if (this._siteIdCache.has(siteUrl)) return this._siteIdCache.get(siteUrl)!;
        const client = await getGraphClient();
        const { hostname, path } = parseSiteUrl(siteUrl);
        const site = await client.api(`/sites/${hostname}:${path}`).select('id').get();
        if (!site?.id) throw new Error(`Cannot resolve site ID for ${siteUrl}`);
        this._siteIdCache.set(siteUrl, site.id);
        return site.id;
    }

    private async _getRegistryListId(siteId: string): Promise<string | null> {
        if (this._registryListIdCache.has(siteId)) return this._registryListIdCache.get(siteId)!;
        const client = await getGraphClient();
        const resp = await client.api(`/sites/${siteId}/lists`)
            .header('Prefer', PREFER_HEADER)
            .filter(`displayName eq '${SP_LIST_NAME_INJIO_REGISTRY}'`)
            .select('id,list,webUrl')
            .get();
        if (!resp?.value?.length) return null;
        const listId: string = resp.value[0].id;
        if (resp.value[0].webUrl) this._registryUrlCache.set(siteId, resp.value[0].webUrl);
        // Self-heal registries provisioned before this list was made hidden.
        if (!resp.value[0].list?.hidden) {
            await client.api(`/sites/${siteId}/lists/${listId}`)
                .patch({ list: { hidden: true } })
                .catch(() => {});
        }
        this._registryListIdCache.set(siteId, listId);
        return listId;
    }


    private _mapRegistryRow(item: any): InjioRegistrySource {
        const f = item.fields ?? {};
        return {
            id: Number(item.id) || 0,
            etag: item['@odata.etag'],
            displayName: f.Title ?? undefined,
            siteId: f.SiteId ?? '',
            webId: f.WebId ?? '',
            listId: f.ListId ?? '',
            sourceListUrl: f.SourceListUrl ?? '',
            sourceType: f.SourceType as InjioRegistrySource['sourceType'],
            cacheListId: f.CacheListId ?? '',
            cacheListUrl: f.CacheListUrl ?? '',
            lastFullSyncDate: f.LastFullSyncDate ?? undefined,
            tagFieldName: f.TagFieldName ?? undefined,
            filterFieldName: f.FilterFieldName ?? undefined,
            filterOperator: f.FilterOperator ?? undefined,
            filterValue: f.FilterValue ?? undefined,
            detailPageUrlTemplate: f.DetailPageUrlTemplate ?? undefined,
            lockOwner: f.LockOwner ?? undefined,
            lockExpiresAt: f.LockExpiresAt ?? undefined,
        };
    }

    private _buildAnnouncementFilter(source: InjioRegistrySource): string | null {
        if (!source.filterFieldName || !source.filterOperator) return null;
        const values = (source.filterValue ?? '').split(',').map(v => v.trim()).filter(Boolean);
        if (!values.length) return null;
        const esc = (v: string) => `'${v.replace(/'/g, "''")}'`;
        if (source.filterOperator === 'Equals')
            return `fields/${source.filterFieldName} eq ${esc(values[0])}`;
        if (source.filterOperator === 'NotEquals')
            return `fields/${source.filterFieldName} ne ${esc(values[0])}`;
        if (source.filterOperator === 'OneOf')
            return values.map(v => `fields/${source.filterFieldName} eq ${esc(v)}`).join(' or ');
        return null;
    }

    private async _fetchAllRegistrySources(
        siteId: string,
        registryListId: string,
    ): Promise<InjioRegistrySource[]> {
        const client = await getGraphClient();
        const sources: InjioRegistrySource[] = [];
        let nextLink: string | undefined = `/sites/${siteId}/lists/${registryListId}/items`;

        while (nextLink) {
            const resp: any = await client.api(nextLink)
                .header('Prefer', PREFER_HEADER)
                .expand('fields')
                .get();
            for (const item of resp?.value ?? []) {
                sources.push(this._mapRegistryRow(item));
            }
            nextLink = resp?.['@odata.nextLink'];
        }
        return sources;
    }

    // Provisions InjioEmbeddings-{sourceListId} on the site if it doesn't exist,
    // and ensures all required columns are present. Idempotent.
    //
    // Never destroys existing data: Graph can't rename/retype a column in
    // place, so a pre-existing list from before a schema change simply keeps
    // its old columns (e.g. DeepSignature, IndexedAt) as dead weight alongside
    // the new ones (DeepVector, CachedDate) added here — same non-destructive
    // approach as the smart-search-indexer POC's hasCacheFieldFor guard.
    private async _provisionCacheList(
        siteId: string,
        sourceListId: string,
    ): Promise<{ cacheListId: string; cacheListUrl: string }> {
        const client = await getGraphClient();
        const displayName = `${SP_INJIO_EMBEDDINGS_LIST_PREFIX}${sourceListId}`;

        const existing = await client.api(`/sites/${siteId}/lists`)
            .header('Prefer', PREFER_HEADER)
            .filter(`displayName eq '${displayName}'`)
            .select('id,webUrl,list')
            .get();

        let cacheListId: string;
        let cacheListUrl: string;

        if (existing?.value?.length) {
            cacheListId = existing.value[0].id;
            cacheListUrl = existing.value[0].webUrl;
            // Self-heal lists provisioned before this list was made hidden.
            if (!existing.value[0].list?.hidden) {
                await client.api(`/sites/${siteId}/lists/${cacheListId}`)
                    .patch({ list: { hidden: true } })
                    .catch(() => {});
            }
        } else {
            const newList = await client.api(`/sites/${siteId}/lists`).post({
                displayName,
                list: { template: 'genericList', hidden: true },
            });
            cacheListId = newList.id;
            cacheListUrl = newList.webUrl;
        }

        const existingCols = await client
            .api(`/sites/${siteId}/lists/${cacheListId}/columns`)
            .select('name')
            .get();
        const existingColNames = new Set<string>(
            (existingCols?.value ?? []).map((c: any) => c.name as string),
        );

        type ColDef = { name: string; displayName: string; def: Record<string, unknown> };
        const cols: ColDef[] = [
            { name: 'SiteId',             displayName: 'Site Id',             def: { text: {} } },
            { name: 'WebId',              displayName: 'Web Id',              def: { text: {} } },
            { name: 'ListId',             displayName: 'List Id',             def: { text: {} } },
            { name: 'ItemId',             displayName: 'Item Id',             def: { number: {} } },
            { name: 'ItemUniqueId',       displayName: 'Item Unique Id',      def: { text: {} } },
            // Choice, not free text — Graph accepts the plain string value on write either way.
            { name: 'SourceType',         displayName: 'Source Type',         def: { choice: { choices: ['Page', 'Announcement', 'ListItem', 'Document'], allowTextEntry: false } } },
            // Text, not number — matches the official schema.
            { name: 'ChunkIndex',         displayName: 'Chunk Index',         def: { text: {} } },
            { name: 'DeepFloat',          displayName: 'Deep Float',          def: { text: { allowMultipleLines: true } } },
            { name: 'DeepVector',         displayName: 'Deep Vector',         def: { text: { allowMultipleLines: true } } },
            { name: 'Keywords',           displayName: 'Keywords',            def: { text: { allowMultipleLines: true } } },
            { name: 'Intent',             displayName: 'Intent',              def: { text: {} } },
            { name: 'IntentKey',          displayName: 'Intent Key',          def: { text: {} } },
            { name: 'Category',           displayName: 'Category',            def: { text: {} } },
            { name: 'CategoryKey',        displayName: 'Category Key',        def: { text: {} } },
            // Text, not number — matches the official schema (same convention as the
            // sibling Copilot pipeline's WV_SearchEmbeddingsCache list).
            { name: 'CategoryConfidence', displayName: 'Category Confidence', def: { text: {} } },
            // Narrow scope: Page News vs. not-News only, not a general content taxonomy.
            { name: 'ContentSubtype',     displayName: 'Content Subtype',     def: { text: {} } },
            { name: 'Tag',                displayName: 'Tag',                 def: { text: {} } },
            { name: 'ModelName',          displayName: 'Model Name',          def: { text: {} } },
            // Pipeline-only for now ('chunked') — reserved for future synthetic-question embeddings.
            { name: 'EmbeddingMode',      displayName: 'Embedding Mode',      def: { text: {} } },
            { name: 'ContentHash',        displayName: 'Content Hash',        def: { text: {} } },
            { name: 'CachedDate',         displayName: 'Cached Date',         def: { dateTime: {} } },
            // Denormalized site URL only — not a link to the specific item.
            { name: 'WebUrl',             displayName: 'Web Url',             def: { text: {} } },
        ];

        for (const col of cols) {
            if (existingColNames.has(col.name)) continue;
            await client.api(`/sites/${siteId}/lists/${cacheListId}/columns`).post({
                name: col.name,
                displayName: col.displayName,
                ...col.def,
            });
        }

        return { cacheListId, cacheListUrl };
    }

    // ── Public API ───────────────────────────────────────────────────────────

    // Re-verifies (and self-heals, including full recreate when the schema is
    // pre-v2) a source's cache list on every index run — not just at
    // registration time. Without this, a source registered before a schema
    // change keeps its stale cache list forever and every write starts failing
    // with Graph's "Field 'X' is not recognized" error, since _provisionCacheList
    // otherwise only ever runs once, at registerAnnouncementSource time.
    // Returns an updated source if the cache list was recreated (new id/url),
    // so callers must use the returned object for the rest of the run.
    async ensureCacheListSchema(
        siteUrl: string,
        source: InjioRegistrySource,
    ): Promise<InjioRegistrySource> {
        const siteId = await this._getSiteId(siteUrl);
        const { cacheListId, cacheListUrl } = await this._provisionCacheList(siteId, source.listId);
        if (cacheListId === source.cacheListId) return source;

        // Cache list was recreated under a new id — persist it on the registry
        // row so future runs (and other sessions) pick up the new list too.
        const registryListId = await this._getRegistryListId(siteId);
        if (registryListId) {
            const client = await getGraphClient();
            await client
                .api(`/sites/${siteId}/lists/${registryListId}/items/${source.id}/fields`)
                .patch({ CacheListId: cacheListId, CacheListUrl: cacheListUrl })
                .catch(() => {});
        }
        return { ...source, cacheListId, cacheListUrl };
    }

    async checkRegistryExists(
        siteUrl: string,
    ): Promise<{ exists: boolean; sources: InjioRegistrySource[]; registryUrl: string | null }> {
        try {
            const siteId = await this._getSiteId(siteUrl);
            const registryListId = await this._getRegistryListId(siteId);
            if (!registryListId) return { exists: false, sources: [], registryUrl: null };
            const sources = await this._fetchAllRegistrySources(siteId, registryListId);
            return { exists: true, sources, registryUrl: this._registryUrlCache.get(siteId) ?? null };
        } catch (e) {
            console.error('[SearchIndexerService] checkRegistryExists failed', e);
            return { exists: false, sources: [], registryUrl: null };
        }
    }

    // Returns the oldest LastFullSyncDate across all sources — the most stale indicator.
    // Returns null if the registry doesn't exist or no source has ever been indexed.
    async getLastSyncDate(siteUrl: string): Promise<string | null> {
        try {
            const siteId = await this._getSiteId(siteUrl);
            const registryListId = await this._getRegistryListId(siteId);
            if (!registryListId) return null;
            const sources = await this._fetchAllRegistrySources(siteId, registryListId);
            if (!sources.length) return null;
            const dates = sources.map(s => s.lastFullSyncDate).filter(Boolean) as string[];
            if (!dates.length) return null;
            return dates.reduce((a, b) => (a < b ? a : b));
        } catch (e) {
            console.warn('[SearchIndexerService] getLastSyncDate failed', e);
            return null;
        }
    }

    async fetchRegistrySources(siteUrl: string): Promise<InjioRegistrySource[]> {
        const siteId = await this._getSiteId(siteUrl);
        const registryListId = await this._getRegistryListId(siteId);
        if (!registryListId) return [];
        return this._fetchAllRegistrySources(siteId, registryListId);
    }

    async fetchSourceItems(siteUrl: string, source: InjioRegistrySource): Promise<SourceItem[]> {
        const siteId = await this._getSiteId(siteUrl);
        const client = await getGraphClient();

        if (source.sourceType === 'Page') {
            // Mirror the POC's fetchPagesWithFields: parallel-fetch the Pages API
            // (for stable page GUIDs + metadata) and the list items (for all SP fields
            // including PromotedState, CanvasContent1, FileLeafRef), then join by filename.
            const sitePagesListId = source.listId;

            const [pages, listItems] = await Promise.all([
                this._fetchAllPages(siteId, client),
                this._fetchAllRawListItems(siteId, sitePagesListId, client),
            ]);

            // Build lookup: FileLeafRef (lowercase) → list item  (POC: byLeaf map)
            const byLeaf = new Map<string, any>();
            for (const item of listItems) {
                const leaf = _leafName(item.fields?.FileLeafRef ?? '');
                if (leaf) byLeaf.set(leaf, item);
            }

            const result: SourceItem[] = [];
            for (const page of pages) {
                const leaf = _leafFromUrl(page.webUrl ?? '');
                const listItem = byLeaf.get(leaf);
                // Merge list item fields (CanvasContent1, version fields, tags, etc.)
                // with page-level metadata, preferring list item values for SP fields
                const mergedFields: Record<string, any> = { ...(listItem?.fields ?? {}) };
                result.push({
                    itemId: listItem ? (Number(listItem.id) || 0) : 0,
                    // page.id is the stable GUID from the Pages API — same as POC
                    itemUniqueId: String(page.id),
                    title: String(page.title ?? listItem?.fields?.Title ?? 'Untitled'),
                    webUrl: String(page.webUrl ?? ''),
                    lastModifiedDateTime: String(page.lastModifiedDateTime ?? ''),
                    fields: mergedFields,
                });
            }

            // Resolve real publishing state from each page's version history (mirrors
            // the POC's publishing.js — a page is "published" only if it has a major
            // (x.0) version, not merely because PromotedState !== 0, which actually
            // just means "posted as News" and is 0 for the vast majority of ordinary
            // pages). Bounded concurrency avoids firing 1000+ simultaneous requests.
            await _mapWithConcurrency(result, 8, async item => {
                const pub = await this._resolvePagePublishState(siteId, sitePagesListId, item.itemId, client);
                item.isPublished = pub.isPublished;
                if (pub.isPublished && pub.publishedContent) {
                    item.fields.CanvasContent1 = pub.publishedContent;
                }
            });

            return result;
        }

        if (source.sourceType === 'Announcement') {
            // Mirror POC's fetchListItems: inline URL params, $expand=fields for all columns,
            // sharepointIds for the stable item GUID. Keep Prefer header — needed when
            // filter queries target non-indexed columns.
            const filter = this._buildAnnouncementFilter(source);
            const params = [
                '$expand=fields',
                '$select=id,webUrl,lastModifiedDateTime,sharepointIds',
                '$top=5000',
            ];
            if (filter) params.push(`$filter=${encodeURIComponent(filter)}`);

            const items: SourceItem[] = [];
            let url: string | undefined =
                `/sites/${siteId}/lists/${source.listId}/items?${params.join('&')}`;

            while (url) {
                const resp: any = await client.api(url)
                    .header('Prefer', PREFER_HEADER)
                    .get();

                for (const item of resp?.value ?? []) {
                    const uniqueId: string =
                        item.sharepointIds?.listItemUniqueId ?? String(item.id);
                    items.push({
                        itemId: Number(item.id) || 0,
                        itemUniqueId: uniqueId,
                        title: String(item.fields?.Title ?? ''),
                        webUrl: String(item.webUrl ?? ''),
                        lastModifiedDateTime: String(item.lastModifiedDateTime ?? ''),
                        fields: item.fields ?? {},
                    });
                }
                url = resp?.['@odata.nextLink'] ?? undefined;
            }
            return items;
        }

        return [];
    }

    // Fetch all pages via the Pages API — returns page GUIDs, titles, webUrls.
    // Mirrors POC's parallel fetch inside fetchPagesWithFields.
    private async _fetchAllPages(siteId: string, client: any): Promise<any[]> {
        const all: any[] = [];
        let url: string | undefined =
            `/sites/${siteId}/pages?$select=id,title,description,webUrl,lastModifiedDateTime,createdDateTime&$top=5000`;
        while (url) {
            const resp: any = await client.api(url).get();
            all.push(...(resp?.value ?? []));
            url = resp?.['@odata.nextLink'] ?? undefined;
        }
        return all;
    }

    // Fetch all list items with full field expansion — used for the Site Pages list
    // to get CanvasContent1, FileLeafRef, version fields, and any configured tag fields.
    private async _fetchAllRawListItems(siteId: string, listId: string, client: any): Promise<any[]> {
        const all: any[] = [];
        let url: string | undefined =
            `/sites/${siteId}/lists/${listId}/items?$expand=fields&$top=5000`;
        while (url) {
            const resp: any = await client.api(url).get();
            all.push(...(resp?.value ?? []));
            url = resp?.['@odata.nextLink'] ?? undefined;
        }
        return all;
    }

    // Determine whether a Site Page has ever been published as a major (x.0) version,
    // by inspecting its version history — mirrors the POC's publishing.js. A page can be
    // fully live with PromotedState = 0 (that field only means "posted as News"), so
    // publish state has to come from version history, not PromotedState.
    private async _resolvePagePublishState(
        siteId: string,
        sitePagesListId: string,
        itemId: number,
        client: any,
    ): Promise<{ isPublished: boolean; publishedContent: string | null }> {
        if (!itemId) return { isPublished: false, publishedContent: null };
        try {
            // A bare $expand=fields omits CanvasContent1 from the response — a known
            // Graph quirk for this large canvas-content column (confirmed against the
            // working fetch in LocalIndexService.ts's Copilot Readiness pipeline, which
            // explicitly $selects it for the same reason). Naming it (plus the version/
            // moderation fields _isPublishedVersionEntry/_selectLatestPublishedMajorVersion
            // actually read) forces Graph to include it.
            const resp: any = await client
                .api(`/sites/${siteId}/lists/${sitePagesListId}/items/${itemId}/versions`)
                .expand('fields($select=CanvasContent1,OData__UIVersionString,_UIVersionString,OData__ModerationStatus)')
                .top(500)
                .get();
            const versions = resp?.value ?? [];
            const latest = _selectLatestPublishedMajorVersion(versions);
            if (!latest) return { isPublished: false, publishedContent: null };
            return { isPublished: true, publishedContent: latest.fields?.CanvasContent1 ?? null };
        } catch {
            return { isPublished: false, publishedContent: null };
        }
    }

    async fetchCacheItems(siteUrl: string, source: InjioRegistrySource): Promise<CacheRow[]> {
        if (!source.cacheListId) return [];
        const siteId = await this._getSiteId(siteUrl);
        const client = await getGraphClient();
        const rows: CacheRow[] = [];
        let nextLink: string | undefined =
            `/sites/${siteId}/lists/${source.cacheListId}/items`;

        while (nextLink) {
            const resp: any = await client.api(nextLink)
                .header('Prefer', PREFER_HEADER)
                .expand('fields($select=ItemUniqueId,ItemId,ChunkIndex,ContentHash,CachedDate)')
                .select('id')
                .get();

            for (const item of resp?.value ?? []) {
                const f = item.fields ?? {};
                rows.push({
                    id: Number(item.id) || 0,
                    itemUniqueId: f.ItemUniqueId ?? '',
                    itemId: Number(f.ItemId) || 0,
                    chunkIndex: Number(f.ChunkIndex) || 0,
                    contentHash: f.ContentHash ?? undefined,
                    cachedDate: f.CachedDate ?? undefined,
                });
            }
            nextLink = resp?.['@odata.nextLink'];
        }
        return rows;
    }

    async writeCacheRow(
        siteUrl: string,
        source: InjioRegistrySource,
        item: SourceItem,
        chunk: SearchChunkResult,
        tag = '',
        modelName = '',
    ): Promise<boolean> {
        if (!source.cacheListId) return false;
        try {
            const siteId = await this._getSiteId(siteUrl);
            const client = await getGraphClient();

            // Page News/not-News only — not a general content taxonomy. PromotedState
            // === 2 is SharePoint's "posted as News" state on Site Pages items (see
            // the publish-state comments on _resolvePagePublishState above).
            const contentSubtype = source.sourceType === 'Page'
                ? (Number(item.fields?.PromotedState) === 2 ? 'News' : 'Page')
                : '';

            await client.api(`/sites/${siteId}/lists/${source.cacheListId}/items`).post({
                fields: {
                    Title: item.itemUniqueId,
                    SiteId: source.siteId,
                    WebId: source.webId,
                    ListId: source.listId,
                    ItemId: item.itemId,
                    ItemUniqueId: item.itemUniqueId,
                    SourceType: source.sourceType,
                    ChunkIndex: String(chunk.chunkIndex),
                    DeepFloat: JSON.stringify(chunk.embedding),
                    DeepVector: chunk.signature,
                    Keywords: chunk.keywords,
                    Intent: chunk.intent,
                    IntentKey: chunk.intentKey,
                    Category: chunk.category,
                    CategoryKey: chunk.categoryKey,
                    CategoryConfidence: Number.isFinite(chunk.categoryConfidence) ? String(chunk.categoryConfidence) : null,
                    ContentSubtype: contentSubtype,
                    Tag: tag,
                    ModelName: modelName,
                    EmbeddingMode: chunk.embeddingMode,
                    ContentHash: chunk.contentHash,
                    CachedDate: new Date().toISOString(),
                    // Item-specific SharePoint URL — a real content link, not the site base URL.
                    WebUrl: item.webUrl,
                },
            });
            return true;
        } catch (e: any) {
            // Logged with enough context to diagnose without reproducing the run —
            // a Graph 400 here almost always names the offending field (e.g. a
            // legacy-typed column left over from before this cache list's schema
            // was last changed; see _provisionCacheList's non-destructive column-add).
            console.error(
                '[SearchIndexerService] writeCacheRow failed', {
                    cacheListId: source.cacheListId,
                    sourceType: source.sourceType,
                    itemUniqueId: item.itemUniqueId,
                    chunkIndex: chunk.chunkIndex,
                    statusCode: e?.statusCode,
                    message: e?.message,
                    body: e?.body,
                    error: e,
                },
            );
            return false;
        }
    }

    async removeCacheRowsForItem(
        siteUrl: string,
        source: InjioRegistrySource,
        itemUniqueId: string,
    ): Promise<void> {
        if (!source.cacheListId) return;
        try {
            const siteId = await this._getSiteId(siteUrl);
            const client = await getGraphClient();
            const resp: any = await client
                .api(`/sites/${siteId}/lists/${source.cacheListId}/items`)
                .header('Prefer', PREFER_HEADER)
                .filter(`fields/ItemUniqueId eq '${itemUniqueId}'`)
                .select('id')
                .get();

            for (const item of resp?.value ?? []) {
                await client
                    .api(`/sites/${siteId}/lists/${source.cacheListId}/items/${item.id}`)
                    .delete();
            }
        } catch (e) {
            console.error('[SearchIndexerService] removeCacheRowsForItem failed', e);
        }
    }

    // ETag-conditional PATCH: claims the lease only if no other session holds it.
    // A 412 response means another session claimed the lock between our read and write.
    async acquireLease(
        siteUrl: string,
        source: InjioRegistrySource,
    ): Promise<{ acquired: boolean; ownedBy?: string }> {
        const siteId = await this._getSiteId(siteUrl);
        const registryListId = await this._getRegistryListId(siteId);
        if (!registryListId) return { acquired: false, ownedBy: 'registry not found' };

        const client = await getGraphClient();

        const item: any = await client
            .api(`/sites/${siteId}/lists/${registryListId}/items/${source.id}`)
            .header('Prefer', PREFER_HEADER)
            .expand('fields($select=LockOwner,LockExpiresAt)')
            .get();

        const etag: string = item['@odata.etag'];
        const f = item.fields ?? {};
        const isLocked =
            f.LockOwner && f.LockExpiresAt && new Date(f.LockExpiresAt) > new Date();

        if (isLocked) return { acquired: false, ownedBy: f.LockOwner };

        const leaseExpiry = new Date(Date.now() + SEARCH_INDEX_LEASE_TTL_MS).toISOString();
        try {
            await client
                .api(`/sites/${siteId}/lists/${registryListId}/items/${source.id}`)
                .header('If-Match', etag)
                .patch({ fields: { LockOwner: this._sessionOwner, LockExpiresAt: leaseExpiry } });
            return { acquired: true };
        } catch (err: any) {
            if (err?.statusCode === 412) return { acquired: false, ownedBy: 'another session' };
            throw err;
        }
    }

    async renewLease(siteUrl: string, source: InjioRegistrySource): Promise<void> {
        const siteId = await this._getSiteId(siteUrl);
        const registryListId = await this._getRegistryListId(siteId);
        if (!registryListId) return;
        const client = await getGraphClient();
        const leaseExpiry = new Date(Date.now() + SEARCH_INDEX_LEASE_TTL_MS).toISOString();
        await client
            .api(`/sites/${siteId}/lists/${registryListId}/items/${source.id}/fields`)
            .patch({ LockExpiresAt: leaseExpiry });
    }

    async releaseLease(siteUrl: string, source: InjioRegistrySource): Promise<void> {
        const siteId = await this._getSiteId(siteUrl);
        const registryListId = await this._getRegistryListId(siteId);
        if (!registryListId) return;
        const client = await getGraphClient();
        await client
            .api(`/sites/${siteId}/lists/${registryListId}/items/${source.id}/fields`)
            .patch({ LockOwner: null, LockExpiresAt: null });
    }

    async updateLastSyncDate(siteUrl: string, source: InjioRegistrySource): Promise<void> {
        const siteId = await this._getSiteId(siteUrl);
        const registryListId = await this._getRegistryListId(siteId);
        if (!registryListId) return;
        const client = await getGraphClient();
        await client
            .api(`/sites/${siteId}/lists/${registryListId}/items/${source.id}/fields`)
            .patch({ LastFullSyncDate: new Date().toISOString() });
    }

    // Provisions InjioEmbeddings-{config.listId} then writes a new InjioSearchRegistry row.
    async registerAnnouncementSource(
        siteUrl: string,
        config: SourceRegistrationRequest,
    ): Promise<InjioRegistrySource> {
        const siteId = await this._getSiteId(siteUrl);
        const registryListId = await this._getRegistryListId(siteId);
        if (!registryListId) throw new Error('InjioSearchRegistry not found on this site');

        // Guards against tenants whose registry was provisioned before a column
        // (e.g. SourceListUrl) existed — otherwise the write below fails with
        // Graph's "Field 'X' is not recognized" error.
        await this._ensureRegistryColumns(siteId, registryListId);

        const { cacheListId, cacheListUrl } = await this._provisionCacheList(siteId, config.listId);

        // Extract webId from the compound Graph site ID ({hostname},{siteGuid},{webGuid})
        const webId = siteId.split(',')[2] ?? siteId;

        const client = await getGraphClient();

        const sourceList = await client
            .api(`/sites/${siteId}/lists/${config.listId}`)
            .select('webUrl')
            .get()
            .catch(() => null);

        const newItem: any = await client
            .api(`/sites/${siteId}/lists/${registryListId}/items`)
            .post({
                fields: {
                    Title: config.listDisplayName || `${config.sourceType}:${config.listId}`,
                    SiteId: siteId,
                    WebId: webId,
                    ListId: config.listId,
                    SourceListUrl: sourceList?.webUrl ?? null,
                    SourceType: config.sourceType,
                    CacheListId: cacheListId,
                    CacheListUrl: cacheListUrl,
                    TagFieldName: config.tagFieldName ?? null,
                    FilterFieldName: config.filterFieldName ?? null,
                    FilterOperator: config.filterOperator ?? null,
                    FilterValue: config.filterValue ?? null,
                    DetailPageUrlTemplate: config.detailPageUrlTemplate,
                },
            });

        return this._mapRegistryRow(newItem);
    }

    async getSiteListColumns(siteUrl: string, listId: string): Promise<ListColumn[]> {
        const siteId = await this._getSiteId(siteUrl);
        const client = await getGraphClient();
        const resp: any = await client
            .api(`/sites/${siteId}/lists/${listId}/columns`)
            .select('id,name,displayName,text,number,boolean,dateTime,choice,lookup')
            .get();

        return (resp?.value ?? [])
            .filter((c: any) => !SYSTEM_COLUMN_NAMES.has(c.name) && !c.name.startsWith('_'))
            .map((c: any): ListColumn => ({
                id: c.id,
                name: c.name,
                displayName: c.displayName ?? c.name,
                columnType: c.lookup
                    ? 'lookup'
                    : c.choice
                    ? (c.choice.allowMultipleSelections ? 'multiChoice' : 'choice')
                    : c.number
                    ? 'number'
                    : c.boolean
                    ? 'boolean'
                    : c.dateTime
                    ? 'dateTime'
                    : 'text',
            }));
    }

    // Idempotent — adds any registry columns that don't exist yet. Called both from
    // provisionRegistry (initial setup) and registerAnnouncementSource (so tenants who
    // provisioned the registry before a new column was added still get it, instead of
    // failing with a Graph "Field 'X' is not recognized" error on write).
    private async _ensureRegistryColumns(
        siteId: string,
        registryListId: string,
        onProgress?: (msg: string) => void,
    ): Promise<void> {
        const report = (msg: string) => { onProgress?.(msg); };
        const client = await getGraphClient();

        const existingCols = await client
            .api(`/sites/${siteId}/lists/${registryListId}/columns`)
            .select('name')
            .get();
        const existingColNames = new Set<string>(
            (existingCols?.value ?? []).map((c: any) => c.name as string),
        );

        type ColDef = { name: string; displayName: string; def: Record<string, unknown> };
        const cols: ColDef[] = [
            { name: 'SiteId',                  displayName: 'Site Id',                  def: { text: {} } },
            { name: 'WebId',                   displayName: 'Web Id',                   def: { text: {} } },
            { name: 'ListId',                  displayName: 'List Id',                  def: { text: {} } },
            { name: 'SourceListUrl',           displayName: 'Source List Url',          def: { text: {} } },
            { name: 'SourceType',              displayName: 'Source Type',              def: { text: {} } },
            { name: 'CacheListId',             displayName: 'Cache List Id',            def: { text: {} } },
            { name: 'CacheListUrl',            displayName: 'Cache List Url',           def: { text: {} } },
            { name: 'LastFullSyncDate',        displayName: 'Last Full Sync Date',      def: { dateTime: {} } },
            { name: 'TagFieldName',            displayName: 'Tag Field Name',           def: { text: {} } },
            { name: 'FilterFieldName',         displayName: 'Filter Field Name',        def: { text: {} } },
            { name: 'FilterOperator',          displayName: 'Filter Operator',          def: { text: {} } },
            { name: 'FilterValue',             displayName: 'Filter Value',             def: { text: {} } },
            { name: 'DetailPageUrlTemplate',   displayName: 'Detail Page Url Template', def: { text: {} } },
            { name: 'LockOwner',               displayName: 'Lock Owner',               def: { text: {} } },
            { name: 'LockExpiresAt',           displayName: 'Lock Expires At',          def: { dateTime: {} } },
        ];

        let added = 0;
        for (const col of cols) {
            if (existingColNames.has(col.name)) continue;
            report(`Adding column: ${col.displayName}…`);
            await client.api(`/sites/${siteId}/lists/${registryListId}/columns`).post({
                name: col.name,
                displayName: col.displayName,
                ...col.def,
            });
            added++;
        }

        report(added > 0 ? `Done. ${added} column${added > 1 ? 's' : ''} added.` : 'Schema is complete.');
    }

    // Idempotent — creates the list if missing, adds any missing columns.
    // Safe to call on already-provisioned sites (handles partial setups).
    async provisionRegistry(
        siteUrl: string,
        onProgress?: (msg: string) => void,
    ): Promise<void> {
        const report = (msg: string) => { onProgress?.(msg); };
        const siteId = await this._getSiteId(siteUrl);
        const client = await getGraphClient();

        report('Checking for existing registry…');
        let registryListId = await this._getRegistryListId(siteId);

        if (!registryListId) {
            report('Creating InjioSearchRegistry list…');
            const newList = await client.api(`/sites/${siteId}/lists`).post({
                displayName: SP_LIST_NAME_INJIO_REGISTRY,
                list: { template: 'genericList', hidden: true },
            });
            registryListId = newList.id as string;
            this._registryListIdCache.set(siteId, registryListId);
        } else {
            report('Registry found. Verifying schema…');
        }

        await this._ensureRegistryColumns(siteId, registryListId, onProgress);
    }

    async getSiteLists(
        siteUrl: string,
    ): Promise<{ id: string; displayName: string; webUrl: string; sourceType: 'Page' | 'Announcement' }[]> {
        const siteId = await this._getSiteId(siteUrl);
        const client = await getGraphClient();

        // --- Step 1: General lists (same inline-param approach as the POC) ---
        // No Prefer header — that is a SharePoint REST hint that confuses this Graph endpoint.
        const resp: any = await client
            .api(`/sites/${siteId}/lists?$select=id,displayName,list,webUrl&$top=200`)
            .get();

        // --- Step 2: Explicit Site Pages lookup ---
        // The webPageLibrary entry does NOT appear in the general /lists response on many
        // tenants (confirmed by real API response — no webPageLibrary template present).
        // The POC solves this via a dedicated $filter=displayName eq 'Site Pages' call,
        // which reliably finds it regardless of hidden / template visibility.
        let sitePagesEntry: { id: string; displayName: string; webUrl: string } | null = null;
        try {
            const spResp: any = await client
                .api(`/sites/${siteId}/lists?$filter=displayName eq 'Site Pages'&$select=id,displayName,webUrl`)
                .get();
            const found = spResp?.value?.[0];
            if (found?.id) {
                sitePagesEntry = {
                    id: found.id as string,
                    displayName: (found.displayName as string) ?? 'Site Pages',
                    webUrl: (found.webUrl as string) ?? '',
                };
            }
        } catch {
            // Non-fatal — site simply may not have a Site Pages library
        }

        // Internal system lists that must never appear in the source picker
        const SYSTEM_LIST_NAMES = new Set([
            SP_LIST_NAME_INJIO_REGISTRY,
            SP_LIST_NAME_EMBEDDINGS,
            SP_LIST_NAME_CORE_SETTINGS,
            'WVHealth_ToolConfig',
            'WVHealth_Audit',
        ]);
        const SYSTEM_LIST_PREFIX = SP_INJIO_EMBEDDINGS_LIST_PREFIX;

        const PAGE_TEMPLATES = new Set(['webPageLibrary', 'sitepages']);
        const ALLOWED_TEMPLATES = new Set([
            'genericList', 'announcements', 'documentLibrary',
            'events', 'tasks', 'links', 'webPageLibrary', 'sitepages',
        ]);

        const raw: any[] = resp?.value ?? [];
        const others = raw
            .filter((l: any) => {
                const name: string = l.displayName ?? '';
                // Strip our own system lists
                if (SYSTEM_LIST_NAMES.has(name)) return false;
                if (name.startsWith(SYSTEM_LIST_PREFIX)) return false;
                const tmpl: string | undefined = l.list?.template;
                // Page-library templates: handled separately via sitePagesEntry
                if (tmpl && PAGE_TEMPLATES.has(tmpl)) return false;
                // Exclude hidden system lists
                if (l.list?.hidden === true) return false;
                // Only include useful content templates
                if (!tmpl) return true;
                return ALLOWED_TEMPLATES.has(tmpl);
            })
            .map((l: any) => ({
                id: l.id as string,
                displayName: (l.displayName as string) ?? l.id,
                webUrl: (l.webUrl as string) ?? '',
                sourceType: 'Announcement' as const,
            }))
            .sort((a, b) => a.displayName.localeCompare(b.displayName));

        // Site Pages always first (explicitly fetched), then sorted lists
        const result: { id: string; displayName: string; webUrl: string; sourceType: 'Page' | 'Announcement' }[] = [];
        if (sitePagesEntry) {
            result.push({ ...sitePagesEntry, sourceType: 'Page' });
        }
        result.push(...others);
        return result;
    }

    async updateTagFieldName(
        siteUrl: string,
        source: InjioRegistrySource,
        fieldName: string,
    ): Promise<void> {
        const siteId = await this._getSiteId(siteUrl);
        const registryListId = await this._getRegistryListId(siteId);
        if (!registryListId) return;
        const client = await getGraphClient();
        await client
            .api(`/sites/${siteId}/lists/${registryListId}/items/${source.id}/fields`)
            .patch({ TagFieldName: fieldName || null });
    }

    // Reads one item's tag value for its source's configured tag field — mirrors
    // the POC's tagColumns.js readTagFromFields exactly: Lookup columns resolve
    // via a second Graph call to the target list/item (Graph only returns
    // {Field}LookupId, never display text, for Lookup fields), multi-value
    // fields join into a single comma-separated string, everything else is
    // read straight off the item's own fields.
    async readItemTag(
        siteUrl: string,
        source: InjioRegistrySource,
        item: SourceItem,
    ): Promise<string> {
        if (!source.tagFieldName) return '';
        const siteId = await this._getSiteId(siteUrl);
        const client = await getGraphClient();
        const meta = await this._getTagFieldMeta(siteId, source.listId, source.tagFieldName, client);

        if (meta.isLookup) {
            const raw = item.fields[`${source.tagFieldName}LookupId`];
            if (raw === null || raw === undefined) return '';
            if (Array.isArray(raw)) {
                const values = await Promise.all(
                    raw.map(id => this._resolveLookupDisplayValue(siteId, meta, id, client)),
                );
                return values.filter(Boolean).join(',');
            }
            return await this._resolveLookupDisplayValue(siteId, meta, raw, client);
        }

        const raw = item.fields[source.tagFieldName];
        if (raw === null || raw === undefined) return '';
        if (Array.isArray(raw)) {
            return raw.map(_normalizeTagValue).filter(Boolean).join(',');
        }
        return _normalizeTagValue(raw);
    }

    // A Lookup column's value doesn't come back as text in $expand=fields — only
    // as `{InternalName}LookupId` (the target item's numeric ID). To resolve it
    // to display text we need the column's target list/field (from its column
    // definition) and then a lookup against that target item. Cached per
    // (listId, tagFieldName) since it never changes mid-run.
    private async _getTagFieldMeta(
        siteId: string,
        listId: string,
        tagFieldName: string,
        client: any,
    ): Promise<TagFieldMeta> {
        const key = `${listId}:${tagFieldName}`;
        const cached = this._tagFieldMetaCache.get(key);
        if (cached) return cached;

        let meta: TagFieldMeta = { isLookup: false };
        try {
            const resp: any = await client
                .api(`/sites/${siteId}/lists/${listId}/columns`)
                .select('id,name,lookup')
                .get();
            const col = (resp?.value ?? []).find((c: any) => c.name === tagFieldName);
            if (col?.lookup) {
                meta = {
                    isLookup: true,
                    targetListId: col.lookup.listId,
                    targetColumnName: col.lookup.columnName || 'Title',
                };
            }
        } catch {
            // Leave as non-lookup default — readItemTag will just read the field directly.
        }
        this._tagFieldMetaCache.set(key, meta);
        return meta;
    }

    private async _resolveLookupDisplayValue(
        siteId: string,
        meta: TagFieldMeta,
        lookupId: any,
        client: any,
    ): Promise<string> {
        if (!lookupId || !meta.targetListId || !meta.targetColumnName) return '';
        const cacheKey = `${meta.targetListId}:${meta.targetColumnName}:${lookupId}`;
        const cached = this._tagLookupValueCache.get(cacheKey);
        if (cached !== undefined) return cached;

        let value = '';
        try {
            const resp: any = await client
                .api(`/sites/${siteId}/lists/${meta.targetListId}/items/${lookupId}`)
                .expand(`fields($select=${meta.targetColumnName})`)
                .get();
            value = _normalizeTagValue(resp?.fields?.[meta.targetColumnName]);
        } catch {
            // Leave as '' — a missing/deleted target item shouldn't break indexing.
        }
        this._tagLookupValueCache.set(cacheKey, value);
        return value;
    }

    async deleteRegistrySource(
        siteUrl: string,
        source: InjioRegistrySource,
    ): Promise<void> {
        const siteId = await this._getSiteId(siteUrl);
        const registryListId = await this._getRegistryListId(siteId);
        if (!registryListId) throw new Error('InjioSearchRegistry not found on this site');
        const client = await getGraphClient();
        await client
            .api(`/sites/${siteId}/lists/${registryListId}/items/${source.id}`)
            .delete();
    }
}

// ── Module helpers ────────────────────────────────────────────────────────────

// Mirrors POC's tagColumns.js normalizeTagValue: coerce any SharePoint field
// value (string, number, boolean, choice/person/taxonomy object) to a plain string.
function _normalizeTagValue(v: any): string {
    if (v === null || v === undefined) return '';
    if (typeof v === 'string') return v.trim();
    if (typeof v === 'number' || typeof v === 'boolean') return String(v);
    if (typeof v === 'object') {
        return String(v.Label || v.Title || v.LookupValue || v.Value || '').trim();
    }
    return String(v).trim();
}

// Mirrors POC's getPageLeafNameFromUrl: strips query string, returns decoded
// lowercase filename (e.g. "our-news.aspx"). Used to join the Pages API results
// with the Site Pages list items by FileLeafRef.
function _leafFromUrl(webUrl: string): string {
    if (!webUrl) return '';
    const noQuery = webUrl.split('?')[0];
    const parts = noQuery.split('/');
    const leaf = parts[parts.length - 1] ?? '';
    try { return decodeURIComponent(leaf).toLowerCase(); } catch { return leaf.toLowerCase(); }
}

// Normalise a FileLeafRef value from a list item field to the same lowercase form.
function _leafName(fileLeafRef: string): string {
    if (!fileLeafRef) return '';
    try { return decodeURIComponent(fileLeafRef).toLowerCase(); } catch { return fileLeafRef.toLowerCase(); }
}

// ── Page publish-state helpers (mirror POC's publishing.js) ─────────────────

function _getVersionString(fields: Record<string, any>): string | null {
    return fields?.OData__UIVersionString ?? fields?._UIVersionString ?? null;
}

function _getVersionMeta(vStr?: string | null): { major: number; minor: number } {
    if (!vStr) return { major: 0, minor: 0 };
    const [maj, min] = String(vStr).split('.');
    return { major: parseInt(maj, 10) || 0, minor: parseInt(min, 10) || 0 };
}

// A version entry is "published" if its moderation status is explicitly Approved (0),
// or — when moderation isn't in play — if its version label is itself a major (x.0).
function _isPublishedVersionEntry(versionItem: any): boolean {
    const fields = versionItem?.fields ?? {};
    const moderation = fields.OData__ModerationStatus;
    if (moderation !== undefined && moderation !== null && moderation !== '') {
        return Number(moderation) === 0;
    }
    const label = _getVersionString(fields) ?? versionItem?.versionLabel ?? '';
    const meta = _getVersionMeta(label);
    return meta.major > 0 && meta.minor === 0;
}

function _selectLatestPublishedMajorVersion(
    versions: any[],
): { meta: { major: number; minor: number }; versionLabel: string; fields: any } | null {
    const majorCandidates = (versions ?? [])
        .map(v => ({ ...v, versionLabel: _getVersionString(v.fields ?? {}) ?? v.versionLabel ?? v.version ?? '' }))
        .map(v => ({ ...v, meta: _getVersionMeta(v.versionLabel) }))
        .filter(v => v.meta.major > 0 && v.meta.minor === 0);
    if (!majorCandidates.length) return null;
    const explicitPublished = majorCandidates.filter(_isPublishedVersionEntry);
    const source = explicitPublished.length ? explicitPublished : majorCandidates;
    source.sort((a, b) => b.meta.major - a.meta.major);
    return source[0] ?? null;
}

// Bounded-concurrency forEach — avoids firing hundreds of simultaneous Graph
// requests (one per page, for version history) at once.
async function _mapWithConcurrency<T>(
    items: T[],
    limit: number,
    fn: (item: T, index: number) => Promise<void>,
): Promise<void> {
    let next = 0;
    async function worker() {
        while (next < items.length) {
            const i = next++;
            await fn(items[i], i);
        }
    }
    await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
}
